import React from "react";

interface GoogleComplianceNoticeProps {
  className?: string;
}

const GoogleComplianceNotice: React.FC<GoogleComplianceNoticeProps> = ({ className = "" }) => {
  return (
    <section className={`w-full py-8 md:py-14 px-6 lg:px-20 flex justify-center bg-white ${className}`}>
      <div className="max-w-[1400px] w-full flex flex-col">
        
        {/* Header Block */}
        <div className="hidden md:flex items-center gap-2 text-neutral-500 uppercase tracking-[0.15em] text-xs font-bold mb-2">
          <span className="w-5 h-[1.5px] bg-neutral-400" />
          <span>Compliance & Transparency</span>
        </div>

        <h2 className="text-2xl sm:text-3xl md:text-4xl font-sans font-bold leading-tight tracking-tight text-neutral-900 text-center md:text-left">
          Policy Disclosures & <span className="text-[#DE3226]">Loan Terms</span>
        </h2>

        <p className="text-neutral-500 text-xs sm:text-sm max-w-3xl mt-2 text-center md:text-left leading-relaxed">
          Transparent short-term loan, payday loan, and personal loan terms in full compliance with Google Ads policies and RBI guidelines.
        </p>

        {/* Top 3 Core Disclosures Grid - Colorful Compact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 mt-6 md:mt-8 items-stretch">
          
          {/* Card 1: Loan Tenure */}
          <div className="bg-gradient-to-br from-[#FFF5F4] to-white border border-[#FBE3DF] rounded-[16px] p-4 sm:p-5 flex flex-col justify-between shadow-[0_2px_12px_rgba(222,50,38,0.05)] hover:border-[#DE3226]/40 transition-all">
            <div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#DE3226]/10 text-[#DE3226] text-[10px] font-black uppercase tracking-wider">
                Tenure Period
              </span>
              <h3 className="text-lg sm:text-xl font-black text-neutral-900 mt-2">
                62 to 180 Days
              </h3>
              <p className="text-xs text-neutral-600 leading-relaxed mt-2">
                Flexible loan repayment terms ranging from a minimum of <strong className="text-[#DE3226]">62 days</strong> up to <strong className="text-[#DE3226]">180 days</strong>.
              </p>
            </div>
          </div>

          {/* Card 2: Repayment Period (Min / Max) */}
          <div className="bg-gradient-to-br from-[#FFF5F4] to-white border border-[#FBE3DF] rounded-[16px] p-4 sm:p-5 flex flex-col justify-between shadow-[0_2px_12px_rgba(222,50,38,0.05)] hover:border-[#DE3226]/40 transition-all">
            <div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#DE3226]/10 text-[#DE3226] text-[10px] font-black uppercase tracking-wider">
                Repayment Timeline
              </span>
              <h3 className="text-lg sm:text-xl font-black text-neutral-900 mt-2">
                Min 62 – Max 180 Days
              </h3>
              <p className="text-xs text-neutral-600 leading-relaxed mt-2">
                <strong>Minimum:</strong> 62 days. <strong>Maximum:</strong> 180 days. No loans require repayment in 60 days or less.
              </p>
            </div>
          </div>

          {/* Card 3: Maximum APR Disclosure */}
          <div className="bg-[#DE3226] text-white rounded-[16px] p-4 sm:p-5 flex flex-col justify-between shadow-[0_4px_16px_rgba(222,50,38,0.22)]">
            <div>
              <div className="flex items-center justify-between">
                <span className="bg-white/20 text-white text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full">
                  Separate Disclosure
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-black text-white mt-2">
                Maximum APR Disclosure
              </h3>
              <p className="text-xs text-white/90 leading-relaxed mt-1.5 font-medium">
                Maximum Annual Percentage Rate (APR) is distinctly disclosed upfront to borrowers prior to loan execution based on credit assessment.
              </p>
            </div>
          </div>

        </div>

        {/* Representative Loan Cost Example */}
        <div className="w-full mt-10 pt-8 border-t border-[#E2E8F0] flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
            <h3 className="text-base sm:text-lg font-bold text-neutral-900">
              Representative Loan-Cost Example
            </h3>
            <span className="text-xs text-neutral-500">
              Tenure: 90 Days (within 62 to 180 days)
            </span>
          </div>

          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
            Illustrative calculation showing the total cost of the loan, including all applicable fees and charges:
          </p>

          <div className="w-full flex flex-col divide-y divide-neutral-100 text-xs sm:text-sm text-neutral-700 mt-1">
            <div className="py-2.5 flex justify-between items-center w-full">
              <span className="text-neutral-500">• Loan Principal Amount:</span>
              <span className="font-semibold text-neutral-800">₹10,000</span>
            </div>
            <div className="py-2.5 flex justify-between items-center w-full">
              <span className="text-neutral-500">• Loan Tenure:</span>
              <span className="font-semibold text-neutral-800">90 Days (3 Months)</span>
            </div>
            <div className="py-2.5 flex justify-between items-center w-full">
              <span className="text-neutral-500">• Total Interest:</span>
              <span className="font-semibold text-neutral-800">₹750</span>
            </div>
            <div className="py-2.5 flex justify-between items-center w-full">
              <span className="text-neutral-500">• Processing Fee:</span>
              <span className="font-semibold text-neutral-800">₹500</span>
            </div>
            <div className="py-2.5 flex justify-between items-center w-full">
              <span className="text-neutral-500">• GST on Processing Fee (18%):</span>
              <span className="font-semibold text-neutral-800">₹90</span>
            </div>
            <div className="py-2.5 flex justify-between items-center w-full">
              <span className="text-neutral-500">• Net Disbursed Amount:</span>
              <span className="font-semibold text-neutral-800">₹9,410</span>
            </div>
            <div className="py-2.5 flex justify-between items-center w-full">
              <span className="text-neutral-500">• Total Repayment Amount:</span>
              <span className="font-semibold text-[#DE3226]">₹10,750</span>
            </div>
            <div className="py-3 flex justify-between items-center w-full font-medium border-t border-neutral-200">
              <span className="text-neutral-800 font-semibold">Total Cost of the Loan:</span>
              <span className="font-bold text-neutral-900">₹1,340 (Interest + Fees + GST)</span>
            </div>
          </div>

          <p className="text-[11px] text-neutral-400 mt-1">
            *Representative example for illustrative purposes. Actual terms, APR, and fees are established upon individual applicant assessment.
          </p>
        </div>

      </div>
    </section>
  );
};

export default GoogleComplianceNotice;
